import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '../../context/NotificationContext';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  Select,
  Typography,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import promoService from '../../services/promoService';
import { useCommon } from '../../context/CommonContext';
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const PromoCampaignList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  useEffect(() => {
    setPageTitle("Promo Campaigns");
  }, [setPageTitle]);
  // State management
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} campaigns`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });

  const getCampaigns = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        ...filters,
        ...params
      };
      
      console.log('Fetching campaigns with params:', requestParams);
      const response = await promoService.getAllCampaigns(requestParams);
      console.log('Campaigns API Response:', response);
      
      if (response.data && response.data.status) {
        const campaignsData = response.data.data || [];
        
        console.log('Campaigns data:', campaignsData);
        
        // Ensure campaignsData is an array
        if (Array.isArray(campaignsData)) {
          setCampaigns(campaignsData);
          setPagination(prev => ({
            ...prev,
            total: campaignsData.length
          }));
        } else {
          console.error('Campaigns data is not an array:', campaignsData);
          setCampaigns([]);
        }
      } else {
        notify.error({ message: 'Failed to fetch campaigns' });
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      notify.error({ message: 'Failed to fetch campaigns' });
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [filters, notify]);

  // Load campaigns data
  useEffect(() => {
    getCampaigns();
  }, [getCampaigns]);

  // Handle search (explicit, e.g. enter / button click)
  const handleSearch = (value) => {
    const trimmed = (value || '').trim();
    setFilters(prev => ({ ...prev, search: trimmed }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getCampaigns({
      search: trimmed,
      status: filters.status
    });
  };

  // Debounced search-as-you-type
  const searchDebounceRef = useRef(null);
  const handleSearchInput = (e) => {
    const value = e.target.value;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getCampaigns({
      search: filters.search,
      status: value
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
  };

  // Handle add campaign
  const handleAddCampaign = () => {
    navigate('/promo-campaign/add/new');
  };

  // Handle edit campaign
  const handleEditCampaign = (pcId) => {
    navigate(`/promo-campaign/edit/${pcId}`);
  };

  // Format date
  const getDateString = (val) => {
    if (val) {
      const dateStr = new Date(val).toLocaleDateString();
      return dateStr;
    } else {
      return 'No Date';
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'pc_name',
      key: 'pc_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.pc_name.localeCompare(b.pc_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Redeems',
      dataIndex: 'no_of_redeem_used',
      key: 'no_of_redeem_used',
      width: 100,
      sorter: (a, b) => (a.no_of_redeem_used || 0) - (b.no_of_redeem_used || 0),
      render: (text) => <Text style={{ fontSize: '14px' }}>{text || 0}</Text>,
    },
    {
      title: 'Start Date',
      dataIndex: 'start',
      key: 'start',
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>
          {getDateString(text)}
        </Text>
      )
    },
    {
      title: 'Expiration',
      dataIndex: 'expiration',
      key: 'expiration',
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>
          {getDateString(text)}
        </Text>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>{text ? new Date(text).toLocaleDateString() : '—'}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditCampaign(record.pc_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  return (
      <Card>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <Title level={4} style={{ margin: 0, flexShrink: 0 }}>
            Campaigns ({pagination.total})
          </Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 280px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Select
              value={filters.status}
              onChange={handleStatusChange}
              placeholder="All"
              style={{ width: 120, flexShrink: 0 }}
              allowClear
            >
              <Option value="">All</Option>
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
              <Option value="EXPIRED">Expired</Option>
            </Select>
            <Search
              placeholder="Search by name or description…"
              allowClear
              onSearch={handleSearch}
              onChange={handleSearchInput}
              style={{ flex: '1 1 160px', maxWidth: 320 }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCampaign}
              size="middle"
            >
              Add
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={campaigns}
          rowKey="pc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No campaigns found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
  );
};

export default PromoCampaignList;